import React, { useState, useMemo, useCallback } from "react";
import { Row, Col, Button, Tag, Dropdown, Badge } from "antd";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import { MoreOutlined, EyeOutlined, FilterOutlined } from "@ant-design/icons";
import MetricsGrid from "@/components/UI/Layout/MetricGrid";
import { useArchivedOrderData } from "./hooks/useArchivedOrderData";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";
import {
  formatCurrency,
  formatCustomerName,
  formatDate,
  getStatusColor,
} from "@/utils/helper";
import BulkOrderDetailsModal from "../BulkOrders/modals/BulkOrderDetails";
import OrderFilterDrawer from "../component/OrderFilterDrawer";

const ArchivedOrdersManager = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [openOrderDetails, setOpenOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Get data for both bulk and catering archived orders
  const bulkArchivedHook = useArchivedOrderData("bulk");
  const cateringArchivedHook = useArchivedOrderData("catering");

  // Get locations for filter
  const { data: locations = [], isLoading: locationLoading } =
    useGetLocationsQuery();

  // Location options for filter
  const locationOptions = useMemo(
    () =>
      locations.map((loc) => ({
        value: loc.id,
        label: `${loc.name} - ${loc.state?.name || ""}`,
      })),
    [locations]
  );

  // Select the appropriate hook based on active tab
  const currentHook =
    activeTab === 0
      ? bulkArchivedHook
      : activeTab === 1
        ? cateringArchivedHook
        : bulkArchivedHook;

  const openDetailsModal = (orderData) => {
    setSelectedOrder(orderData);
    setOpenOrderDetails(true);
  };

  const closeOrderDetailsModal = () => {
    setOpenOrderDetails(false);
    setSelectedOrder(null);
  };

  // Map order data to table format
  const mapOrderData = useCallback((orders) => {
    if (!orders || orders.length === 0) return [];

    return orders.map((order) => ({
      key: order.id.toString(),
      orderId: order.reference,
      customer: formatCustomerName(order.user),
      orderDate: formatDate(order.created_at),
      branch: order.location?.name || "Unknown Location",
      orderDueDate: formatDate(order.delivery_date),
      amount: formatCurrency(order.amount || 0),
      items: order.items_count || 0,
      status: order.status?.name || "Unknown",
      originalData: order,
    }));
  }, []);

  // Mapped data for each tab
  const bulkOrdersData = useMemo(
    () => mapOrderData(bulkArchivedHook.archivedOrdersData),
    [bulkArchivedHook.archivedOrdersData, mapOrderData]
  );

  const cateringOrdersData = useMemo(
    () => mapOrderData(cateringArchivedHook.archivedOrdersData),
    [cateringArchivedHook.archivedOrdersData, mapOrderData]
  );

  // Tab configuration
  const archivedOrdersTabs = useMemo(
    () => [
      {
        key: "bulkOrders",
        label: "Bulk Order",
        count: bulkArchivedHook.paginationInfo.total,
      },
      {
        key: "EventOrders",
        label: "Event Catering",
        count: cateringArchivedHook.paginationInfo.total,
      },
    ],
    [
      bulkArchivedHook.paginationInfo.total,
      cateringArchivedHook.paginationInfo.total,
    ]
  );

  // Get current tab data
  const currentTabData = useMemo(() => {
    switch (activeTab) {
      case 0:
        return bulkOrdersData;
      case 1:
        return cateringOrdersData;
      default:
        return [];
    }
  }, [activeTab, bulkOrdersData, cateringOrdersData]);

  // Get current loading state
  const isLoading = useMemo(() => {
    switch (activeTab) {
      case 0:
        return bulkArchivedHook.isLoading || bulkArchivedHook.isFetching;
      case 1:
        return (
          cateringArchivedHook.isLoading || cateringArchivedHook.isFetching
        );
      default:
        return false;
    }
  }, [
    activeTab,
    bulkArchivedHook.isLoading,
    bulkArchivedHook.isFetching,
    cateringArchivedHook.isLoading,
    cateringArchivedHook.isFetching,
  ]);

  // Calculate metrics based on current tab
  const orderMetrics = useMemo(() => {
    const data = currentTabData;
    const completedOrders = data.filter(
      (order) =>
        order.status === "Delivered" ||
        order.status === "Picked Up" ||
        order.status === "Completed"
    ).length;
    const cancelledOrders = data.filter(
      (order) => order.status === "Cancelled"
    ).length;
    const totalItems = data.reduce((sum, order) => sum + (order.items || 0), 0);

    return [
      {
        title: "Total Archived Orders",
        value: currentHook.paginationInfo.total.toString(),
        change: "0",
        trend: "none",
      },
      {
        title: "Completed Orders",
        value: completedOrders.toString(),
        change: "0",
        trend: "none",
      },
      {
        title: "Cancelled Orders",
        value: cancelledOrders.toString(),
        change: "0",
        trend: "none",
      },
      {
        title: "Total Items",
        value: totalItems.toString(),
        change: "0",
        trend: "none",
      },
    ];
  }, [currentTabData, currentHook.paginationInfo]);

  // Handle tab change
  const handleTabChange = (newTab) => {
    // console.log("Tab changing from", activeTab, "to", newTab);
    setActiveTab(newTab);
    setSelectedRowKeys([]);
  };

  // Action menu items
  const getActionMenuItems = (record) => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "View Details",
      onClick: () => openDetailsModal(record),
    },
  ];

  // Table columns configuration
  const ordersColumns = [
    {
      title: "Order Id",
      dataIndex: "orderId",
      key: "orderId",
      width: 120,
      render: (text) => <span className="text-gray-600 ">{text}</span>,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 160,
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Date and Time of Order",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 180,
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branch",
      width: 120,
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Order due by",
      dataIndex: "orderDueDate",
      key: "orderDueDate",
      width: 180,
      render: (text) => <span className="text-gray-600 ">{text}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (text) => <span className="text-gray-600 ">{text}</span>,
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items",
      width: 80,
      align: "center",
      render: (text) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const { color, backgroundColor } = getStatusColor(status);
        return (
          <Tag
            style={{
              color,
              backgroundColor,
              border: `1px solid ${color}20`,
              borderRadius: "8px",
              padding: "2px 8px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getActionMenuItems(record),
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            className="text-gray-400 hover:text-gray-600"
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="px-6 bg-gray-50 min-h-full">
      <MetricsGrid data={orderMetrics} />
      <Row>
        <Col span={24}>
          <CustomDataTable
            title="Past Orders"
            data={currentTabData}
            columns={ordersColumns}
            tabs={archivedOrdersTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            showSearch={true}
            searchPlaceholder="Search archived orders..."
            searchMode="server"
            searchValue={currentHook.search}
            onServerSearch={currentHook.handleSearch}
            actionButtons={[]}
            showFilter={true}
            onFilterClick={() => setFilterDrawerOpen(true)}
            filterButton={
              <Badge dot={currentHook.hasActiveFilters} offset={[-4, 4]}>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFilterDrawerOpen(true)}
                  className={`${
                    currentHook.hasActiveFilters
                      ? "border-orange-500 text-orange-500 bg-orange-50"
                      : ""
                  }`}
                >
                  Filter
                </Button>
              </Badge>
            }
            showCSV={true}
            loading={isLoading}
            paginationMode="server"
            currentPage={currentHook.paginationInfo.current_page}
            totalItems={currentHook.paginationInfo.total}
            serverPageSize={currentHook.paginationInfo.per_page}
            onPageChange={currentHook.handlePageChange}
            onPageSizeChange={currentHook.handlePageSizeChange}
            pageSizeOptions={["10", "20", "50", "100"]}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            selectedRowKeys={selectedRowKeys}
          />
        </Col>
      </Row>

      <OrderFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={currentHook.filters}
        onFilterChange={currentHook.handleFilterChange}
        onClearFilters={currentHook.handleClearFilters}
        hasActiveFilters={currentHook.hasActiveFilters}
        locationOptions={locationOptions}
        locationLoading={locationLoading}
      />

      <BulkOrderDetailsModal
        open={openOrderDetails}
        onClose={closeOrderDetailsModal}
        orderData={selectedOrder}
      />
    </div>
  );
};

export default ArchivedOrdersManager;
